import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './AdminPanel.css';
import { Editor } from '@tinymce/tinymce-react';

const AdminPanel = ({ data, onSave, onClose }) => {
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeName, setNodeName] = useState('');
  const [nodeContent, setNodeContent] = useState('');
  const [isUrl, setIsUrl] = useState(false);
  const [error, setError] = useState(null);
  const [linkSource, setLinkSource] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkMode, setLinkMode] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [showNodeForm, setShowNodeForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ saving: false, success: false, error: null });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    // Load initial data from props instead of directly from Firebase
    if (data && data.nodes && data.links) {
      setNodes(data.nodes);
      setLinks(data.links);
      setIsLoading(false);
    }
  }, [data]);

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    setNodeName(node.name);
    setNodeContent(node.content || '');
    setIsUrl(isValidUrl(node.content));
  };

  const handleAddNode = () => {
    const newNode = {
      id: `node-${Date.now()}`,
      name: 'New Node',
      content: '',
      type: 'primary'
    };
    const updatedNodes = [...nodes, newNode];
    setNodes(updatedNodes);
    handleNodeSelect(newNode);
    
    // Save changes after adding a node
    setTimeout(() => handleSave(), 100);
  };

  const handleDeleteNode = (nodeId) => {
    const updatedNodes = nodes.filter(node => node.id !== nodeId);
    const updatedLinks = links.filter(link => 
      link.source !== nodeId && link.target !== nodeId
    );
    
    setNodes(updatedNodes);
    setLinks(updatedLinks);
    
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode(null);
      setNodeName('');
      setNodeContent('');
      setIsUrl(false);
    }
    
    // Save changes after deleting a node
    setTimeout(() => handleSave(), 100);
  };

  const handleUpdateNode = () => {
    if (!selectedNode) return;
    
    const updatedNodes = nodes.map(node => {
      if (node.id === selectedNode.id) {
        return {
          ...node,
          name: nodeName,
          content: nodeContent
        };
      }
      return node;
    });
    
    setNodes(updatedNodes);
    setSelectedNode({
      ...selectedNode,
      name: nodeName,
      content: nodeContent
    });
    
    // Auto-save changes after updating a node
    handleSave();
  };

  const handleSave = async () => {
    try {
      // Clean up the data to ensure it's serializable
      const cleanData = {
        nodes: nodes.map(node => ({
          id: node.id,
          name: node.name || 'Unnamed Node',
          content: node.content || '',
          type: node.type || 'secondary'
        })),
        links: links.map(link => ({
          id: link.id || `link-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          source: typeof link.source === 'object' ? link.source.id : link.source,
          target: typeof link.target === 'object' ? link.target.id : link.target
        }))
      };

      console.log('Saving data via parent component:', cleanData);
      // Use the onSave prop to let the parent component handle the Firebase saving
      onSave(cleanData);
      
      // Show success message
      console.log('Data successfully passed to parent for saving');
    } catch (error) {
      console.error('Error preparing data for save:', error);
      setError(`Failed to save: ${error.message}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const isValidUrl = (string) => {
    if (!string) return false;
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleContentChange = (content) => {
    setNodeContent(content);
    setIsUrl(isValidUrl(content));
  };
  
  // Handle file upload to Firebase Storage
  const handleFileUpload = async (blobInfo) => {
    try {
      setUploading(true);
      const file = blobInfo.blob();
      const fileRef = ref(storage, `uploads/${Date.now()}-${blobInfo.filename()}`);
      
      // Upload file to Firebase Storage
      await uploadBytes(fileRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(fileRef);
      setUploading(false);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploading(false);
      return '';
    }
  };
  
  const toggleLinkMode = () => {
    setLinkMode(!linkMode);
    setLinkSource(null);
    setLinkTarget(null);
  };
  
  const handleNodeLinkSelect = (node) => {
    if (!linkMode) return;
    
    if (!linkSource) {
      setLinkSource(node);
    } else {
      setLinkTarget(node);
    }
  };
  
  const createLink = () => {
    if (!linkSource || !linkTarget || linkSource.id === linkTarget.id) {
      setError('Please select two different nodes to link');
      return;
    }
    
    // Check if link already exists
    const linkExists = links.some(link => 
      (link.source === linkSource.id && link.target === linkTarget.id) ||
      (link.source.id === linkSource.id && link.target.id === linkTarget.id) ||
      (link.source === linkTarget.id && link.target === linkSource.id) ||
      (link.source.id === linkTarget.id && link.target.id === linkSource.id)
    );
    
    if (linkExists) {
      setError('Link already exists between these nodes');
      return;
    }
    
    // Create the new link
    const newLink = {
      source: linkSource.id,
      target: linkTarget.id,
      id: `link-${Date.now()}`
    };
    
    setLinks([...links, newLink]);
    setLinkSource(null);
    setLinkTarget(null);
    setError(null);
    
    // Save changes after creating a link
    setTimeout(() => handleSave(), 100);
  };
  
  const deleteLink = (sourceId, targetId) => {
    const updatedLinks = links.filter(link => {
      // Handle both object and string references in links
      const linkSource = typeof link.source === 'object' ? link.source.id : link.source;
      const linkTarget = typeof link.target === 'object' ? link.target.id : link.target;
      
      // Check if this is the link we want to delete (in either direction)
      return !((linkSource === sourceId && linkTarget === targetId) || 
               (linkSource === targetId && linkTarget === sourceId));
    });
    
    setLinks(updatedLinks);
    
    // Save changes after deleting a link
    setTimeout(() => handleSave(), 100);
  };

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      {error && <div className="error-message">{error}</div>}
      
      <div className="link-mode-toggle">
        <button 
          className={`link-mode-btn ${linkMode ? 'active' : ''}`} 
          onClick={toggleLinkMode}
        >
          {linkMode ? 'Exit Link Mode' : 'Create Links'}
        </button>
        {linkMode && (
          <div className="link-status">
            <p>
              {!linkSource ? 'Select first node' : 
               !linkTarget ? `Selected: ${linkSource.name} → Select second node` : 
               `${linkSource.name} → ${linkTarget.name}`}
            </p>
            {linkSource && linkTarget && (
              <button className="create-link-btn" onClick={createLink}>Create Link</button>
            )}
          </div>
        )}
      </div>
      
      <div className="admin-panel-content">
        <div className="node-list">
          <h3>Nodes</h3>
          <button className="add-node-btn" onClick={handleAddNode}>Add Node</button>
          <ul>
            {nodes.map(node => (
              <li 
                key={node.id} 
                className={`${selectedNode && selectedNode.id === node.id ? 'selected' : ''} ${linkSource && linkSource.id === node.id ? 'link-source' : ''} ${linkTarget && linkTarget.id === node.id ? 'link-target' : ''}`}
                onClick={() => linkMode ? handleNodeLinkSelect(node) : handleNodeSelect(node)}
              >
                <span className="node-type-indicator" style={{ 
                  backgroundColor: node.type === 'primary' ? '#6B5BA6' : 
                                   node.type === 'tertiary' ? '#909090' : '#1D201F',
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  marginRight: '5px'
                }}></span>
                {node.name}
                <button 
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteNode(node.id);
                  }}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="links-list">
          <h3>Links</h3>
          {links.length === 0 ? (
            <p>No links yet. Use 'Create Links' to connect nodes.</p>
          ) : (
            <ul>
              {links.map(link => {
                // Find the source and target node names
                const sourceNode = nodes.find(n => n.id === (link.source.id || link.source));
                const targetNode = nodes.find(n => n.id === (link.target.id || link.target));
                
                return (
                  <li key={link.id || `${link.source}-${link.target}`}>
                    {sourceNode?.name || 'Unknown'} → {targetNode?.name || 'Unknown'}
                    <button 
                      className="delete-btn"
                      onClick={() => deleteLink(link.source.id || link.source, link.target.id || link.target)}
                    >
                      Delete
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        
        <div className="node-editor">
          {selectedNode ? (
            <>
              <h3>Edit Node</h3>
              <div className="form-group">
                <label>Name:</label>
                <input 
                  type="text" 
                  value={nodeName} 
                  onChange={(e) => setNodeName(e.target.value)}
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label>Content:</label>
                <Editor
                  apiKey="vco5a7pw80i7yznp09h1ejyw4hd5yji9lx9g7ip2cdtx2llt"
                  onInit={(evt, editor) => editorRef.current = editor}
                  value={nodeContent}
                  onEditorChange={handleContentChange}
                  init={{
                    height: 300,
                    menubar: true,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
                      'emoticons', 'template', 'paste', 'importcss', 'autosave',
                      'directionality', 'noneditable', 'visualchars', 'pagebreak',
                      'quickbars', 'codesample'
                    ],
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic forecolor backcolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'removeformat | image media link codesample emoticons | help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    images_upload_handler: handleFileUpload,
                    file_picker_types: 'image media',
                    automatic_uploads: true,
                    quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
                    quickbars_insert_toolbar: 'image media table',
                    contextmenu: 'link image table',
                  }}
                />
                {uploading && <div className="upload-progress">Uploading... {uploadProgress}%</div>}
              </div>
              
              <div className="form-group">
                <label>Type:</label>
                <select 
                  value={selectedNode.type} 
                  onChange={(e) => {
                    const updatedNode = {...selectedNode, type: e.target.value};
                    setSelectedNode(updatedNode);
                    const updatedNodes = nodes.map(node => 
                      node.id === selectedNode.id ? updatedNode : node
                    );
                    setNodes(updatedNodes);
                  }}
                  className="form-control"
                >
                  <option value="primary">Primary (Purple)</option>
                  <option value="secondary">Secondary (Black)</option>
                  <option value="tertiary">Tertiary (Light Gray)</option>
                </select>
              </div>
              {isUrl && (
                <div className="form-group">
                  <p>Preview:</p>
                  <a href={nodeContent} target="_blank" rel="noopener noreferrer" className="url-preview">
                    {nodeContent}
                  </a>
                </div>
              )}
              <button onClick={handleUpdateNode} className="update-btn">Update Node</button>
            </>
          ) : (
            <p>Select a node to edit</p>
          )}
        </div>
      </div>
      
      <div className="admin-panel-actions">
        <button className="save-btn" onClick={handleSave}>Save Changes</button>
        <button className="close-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default AdminPanel;
